import { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import {
  Loader2,
  Save,
  Shield,
  FileCheck,
  Truck,
  FileText,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  PlayCircle,
  Clock,
} from 'lucide-react';

interface AdditionalGstin {
  gstin: string;
  tradeName?: string;
  state?: string;
}

interface GstSettingsState {
  // Read-only — sourced from CompanyProfile
  gstin: string | null;
  companyName: string | null;

  // Per-tenant client portal credentials
  gstPortalUsername: string;
  hasPassword: boolean;

  // API access status (manually marked by accountant after gst.gov.in enable)
  apiAccessEnabled: boolean;
  apiAccessExpiresAt: string | null; // ISO

  // Last verified probe
  lastVerifiedAt: string | null;

  // Feature toggles
  eInvoiceEnabled: boolean;
  eInvoiceAutoGenerate: boolean;
  eInvoiceThreshold: number;
  eWayBillEnabled: boolean;
  eWayBillAutoGenerate: boolean;
  eWayBillThreshold: number;
  eWayBillMandateAll: boolean;
  gstFilingEnabled: boolean;

  // Optional additional GSTINs (multi-state branches)
  additionalGstins: AdditionalGstin[];
}

const EMPTY_STATE: GstSettingsState = {
  gstin: null,
  companyName: null,
  gstPortalUsername: '',
  hasPassword: false,
  apiAccessEnabled: false,
  apiAccessExpiresAt: null,
  lastVerifiedAt: null,
  eInvoiceEnabled: false,
  eInvoiceAutoGenerate: false,
  eInvoiceThreshold: 0,
  eWayBillEnabled: false,
  eWayBillAutoGenerate: false,
  eWayBillThreshold: 50000,
  eWayBillMandateAll: false,
  gstFilingEnabled: false,
  additionalGstins: [],
};

const ALLOWED_ROLES = ['admin', 'owner', 'accountant'];

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isExpiringSoon(iso: string | null): boolean {
  if (!iso) return false;
  const due = new Date(iso).getTime();
  if (Number.isNaN(due)) return false;
  const now = Date.now();
  const fiveDaysMs = 5 * 24 * 60 * 60 * 1000;
  return due > now && due - now < fiveDaysMs;
}

function isExpired(iso: string | null): boolean {
  if (!iso) return false;
  const due = new Date(iso).getTime();
  if (Number.isNaN(due)) return false;
  return due <= Date.now();
}

export function GSTComplianceTab() {
  const { user } = useAuthStore();
  const userRole = (user?.role?.name || '').toLowerCase();
  const canAccess = ALLOWED_ROLES.includes(userRole);

  const [state, setState] = useState<GstSettingsState>(EMPTY_STATE);
  const [pendingPassword, setPendingPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchConfig();
  }, []);

  async function fetchConfig() {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/gst-compliance/config');
      const d = res.data.data;
      setState({
        gstin: d.gstin ?? null,
        companyName: d.companyName ?? null,
        gstPortalUsername: d.gstPortalUsername ?? '',
        hasPassword: !!d.hasPassword,
        apiAccessEnabled: !!d.apiAccessEnabled,
        apiAccessExpiresAt: d.apiAccessExpiresAt ?? null,
        lastVerifiedAt: d.lastVerifiedAt ?? null,
        eInvoiceEnabled: !!d.eInvoiceEnabled,
        eInvoiceAutoGenerate: !!d.eInvoiceAutoGenerate,
        eInvoiceThreshold: Number(d.eInvoiceThreshold ?? 0),
        eWayBillEnabled: !!d.eWayBillEnabled,
        eWayBillAutoGenerate: !!d.eWayBillAutoGenerate,
        eWayBillThreshold: Number(d.eWayBillThreshold ?? 50000),
        eWayBillMandateAll: !!d.eWayBillMandateAll,
        gstFilingEnabled: !!d.gstFilingEnabled,
        additionalGstins: Array.isArray(d.additionalGstins) ? d.additionalGstins : [],
      });
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load GST settings.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    try {
      setSaving(true);
      setError(null);
      const payload: Record<string, unknown> = {
        gstPortalUsername: state.gstPortalUsername || null,
        apiAccessEnabled: state.apiAccessEnabled,
        apiAccessExpiresAt: state.apiAccessExpiresAt,
        eInvoiceEnabled: state.eInvoiceEnabled,
        eInvoiceAutoGenerate: state.eInvoiceAutoGenerate,
        eInvoiceThreshold: state.eInvoiceThreshold,
        eWayBillEnabled: state.eWayBillEnabled,
        eWayBillAutoGenerate: state.eWayBillAutoGenerate,
        eWayBillThreshold: state.eWayBillThreshold,
        eWayBillMandateAll: state.eWayBillMandateAll,
        gstFilingEnabled: state.gstFilingEnabled,
        additionalGstins: state.additionalGstins,
      };
      if (pendingPassword) {
        payload.gstPortalPassword = pendingPassword;
      }
      await api.put('/gst-compliance/config', payload);
      setPendingPassword('');
      setSavedAt(Date.now());
      await fetchConfig();
      setTimeout(() => setSavedAt(null), 2500);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save GST settings.');
    } finally {
      setSaving(false);
    }
  }

  async function handleCheckAccess() {
    try {
      setChecking(true);
      setCheckResult(null);
      setError(null);
      const res = await api.post('/gst-compliance/test-connection');
      setCheckResult({
        ok: !!res.data.success,
        message: res.data.data?.message || (res.data.success ? 'Connected.' : 'Connection failed.'),
      });
      if (res.data.success && res.data.data?.lastVerifiedAt) {
        setState(s => ({ ...s, lastVerifiedAt: res.data.data.lastVerifiedAt }));
      }
    } catch (err: any) {
      setCheckResult({
        ok: false,
        message: err.response?.data?.error || err.message || 'Check failed.',
      });
    } finally {
      setChecking(false);
    }
  }

  function updateAdditionalGstin(idx: number, patch: Partial<AdditionalGstin>) {
    setState(s => ({
      ...s,
      additionalGstins: s.additionalGstins.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    }));
  }

  function addAdditionalGstin() {
    setState(s => ({
      ...s,
      additionalGstins: [...s.additionalGstins, { gstin: '', tradeName: '', state: '' }],
    }));
  }

  function removeAdditionalGstin(idx: number) {
    setState(s => ({
      ...s,
      additionalGstins: s.additionalGstins.filter((_, i) => i !== idx),
    }));
  }

  if (!canAccess) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <Shield size={40} className="mb-3" />
        <p className="text-sm font-medium">Access Restricted</p>
        <p className="text-xs">Only Owner, Admin, and Accountant roles can manage GST settings.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  const inp =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]';
  const lbl = 'mb-1 block text-sm font-medium text-gray-700';
  const expired = isExpired(state.apiAccessExpiresAt);
  const expiringSoon = isExpiringSoon(state.apiAccessExpiresAt);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">GST Settings</h2>
          <p className="text-sm text-gray-500">
            Per-tenant GST portal credentials, API access status, and WhiteBooks GSP toggles.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {savedAt ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {/* ── Company GSTIN summary (read-only) ───────────────────────────── */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-3 flex items-center gap-2">
            <FileText size={18} className="text-[var(--ink-0)]" />
            <h3 className="text-sm font-semibold text-gray-900">Company GSTIN</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <p className={lbl}>Legal Name</p>
              <p className="text-sm text-gray-900">{state.companyName ?? '—'}</p>
            </div>
            <div>
              <p className={lbl}>GSTIN</p>
              <p className="font-mono text-sm text-gray-900">{state.gstin ?? '—'}</p>
              {!state.gstin && (
                <p className="mt-1 text-xs text-amber-600">
                  Set the company GSTIN in Settings &rarr; Company Profile.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── Portal credentials ──────────────────────────────────────────── */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-3 flex items-center gap-2">
            <Shield size={18} className="text-[var(--ink-0)]" />
            <h3 className="text-sm font-semibold text-gray-900">GST Portal Credentials</h3>
          </div>
          <p className="mb-4 text-xs text-gray-500">
            Stored encrypted (AES-256). Required by WhiteBooks GSP to fetch IRN, generate e-Way Bills, and file
            returns on this tenant's behalf. Enable API access on the GST portal at gst.gov.in &rarr; My Profile
            &rarr; Manage API Access (valid 30 days; renew on expiry).
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={lbl}>GST Portal Username</label>
              <input
                className={inp}
                value={state.gstPortalUsername}
                onChange={e => setState(s => ({ ...s, gstPortalUsername: e.target.value }))}
                placeholder="e.g. VEDAAGRO_API"
              />
            </div>
            <div>
              <label className={lbl}>GST Portal Password</label>
              <div className="relative">
                <input
                  className={inp}
                  type={showPassword ? 'text' : 'password'}
                  value={pendingPassword}
                  onChange={e => setPendingPassword(e.target.value)}
                  placeholder={state.hasPassword ? '•••••••• (encrypted)' : 'Enter GST portal password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {state.hasPassword && !pendingPassword && (
                <p className="mt-1 text-xs text-green-600">
                  Password is set. Leave blank to keep current password.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── API access status ───────────────────────────────────────────── */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={18} className="text-[var(--ink-0)]" />
              <h3 className="text-sm font-semibold text-gray-900">API Access Status</h3>
            </div>
            <button
              type="button"
              onClick={handleCheckAccess}
              disabled={checking || !state.gstPortalUsername || (!state.hasPassword && !pendingPassword)}
              className="flex items-center gap-2 rounded-lg border border-[var(--ink-0)] px-3 py-1.5 text-xs font-medium text-[var(--ink-0)] hover:bg-gray-50 disabled:opacity-40"
              title="Run WhiteBooks auth probe with the stored credentials"
            >
              {checking ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />}
              Check API Access
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={lbl}>API Access Enabled at gst.gov.in</label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={state.apiAccessEnabled}
                  onChange={e => setState(s => ({ ...s, apiAccessEnabled: e.target.checked }))}
                />
                <span>Yes — I have enabled API access on the GST portal.</span>
              </label>
            </div>
            <div>
              <label className={lbl}>Expires On (30 days from enable)</label>
              <input
                type="date"
                className={inp}
                value={state.apiAccessExpiresAt ? state.apiAccessExpiresAt.slice(0, 10) : ''}
                onChange={e =>
                  setState(s => ({
                    ...s,
                    apiAccessExpiresAt: e.target.value ? new Date(e.target.value).toISOString() : null,
                  }))
                }
              />
              {expired && (
                <p className="mt-1 flex items-center gap-1 text-xs text-red-600">
                  <XCircle size={12} /> API access has expired. Re-enable on gst.gov.in.
                </p>
              )}
              {!expired && expiringSoon && (
                <p className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                  <AlertTriangle size={12} /> Expires within 5 days.
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-700">
            <Clock size={14} className="text-gray-500" />
            <span>
              Last verified: <strong>{formatDateTime(state.lastVerifiedAt)}</strong>
            </span>
          </div>

          {checkResult && (
            <div
              className={`mt-3 rounded-lg border px-4 py-3 text-sm ${
                checkResult.ok
                  ? 'border-green-200 bg-green-50 text-green-700'
                  : 'border-red-200 bg-red-50 text-red-700'
              }`}
            >
              {checkResult.ok ? (
                <CheckCircle2 size={14} className="mr-1 inline" />
              ) : (
                <XCircle size={14} className="mr-1 inline" />
              )}
              {checkResult.message}
            </div>
          )}
        </div>

        {/* ── e-Invoice ───────────────────────────────────────────────────── */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-4 flex items-center gap-2">
            <FileCheck size={18} className="text-[var(--ink-0)]" />
            <h3 className="text-sm font-semibold text-gray-900">e-Invoice (IRN)</h3>
          </div>
          <div className="space-y-4">
            <ToggleRow
              label="Enable e-Invoice generation"
              description="Generate IRN via WhiteBooks GSP for GST-compliant e-Invoices."
              checked={state.eInvoiceEnabled}
              onChange={() => setState(s => ({ ...s, eInvoiceEnabled: !s.eInvoiceEnabled }))}
            />
            <ToggleRow
              label="Auto-generate IRN on invoice confirmation"
              description="Trigger IRN generation immediately when an invoice is confirmed (blocks confirmation on failure)."
              checked={state.eInvoiceAutoGenerate}
              onChange={() => setState(s => ({ ...s, eInvoiceAutoGenerate: !s.eInvoiceAutoGenerate }))}
              disabled={!state.eInvoiceEnabled}
            />
            <div>
              <label className={lbl}>Threshold (₹) — invoices ≥ this value require IRN</label>
              <input
                type="number"
                min={0}
                className={inp}
                value={state.eInvoiceThreshold}
                onChange={e => setState(s => ({ ...s, eInvoiceThreshold: Number(e.target.value) || 0 }))}
              />
            </div>
          </div>
        </div>

        {/* ── e-Way Bill ──────────────────────────────────────────────────── */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-4 flex items-center gap-2">
            <Truck size={18} className="text-[var(--ink-0)]" />
            <h3 className="text-sm font-semibold text-gray-900">e-Way Bill</h3>
          </div>
          <div className="space-y-4">
            <ToggleRow
              label="Enable e-Way Bill generation"
              description="Generate e-Way Bills via WhiteBooks for goods movement above the threshold."
              checked={state.eWayBillEnabled}
              onChange={() => setState(s => ({ ...s, eWayBillEnabled: !s.eWayBillEnabled }))}
            />
            <ToggleRow
              label="Auto-generate e-Way Bill on dispatch"
              description="Trigger e-Way Bill generation when a dispatch is recorded (blocks dispatch on failure)."
              checked={state.eWayBillAutoGenerate}
              onChange={() => setState(s => ({ ...s, eWayBillAutoGenerate: !s.eWayBillAutoGenerate }))}
              disabled={!state.eWayBillEnabled}
            />
            <ToggleRow
              label="Mandate e-Way Bill for ALL inter-state dispatches"
              description="Override threshold for inter-state movement (some states require e-Way Bill at lower limits)."
              checked={state.eWayBillMandateAll}
              onChange={() => setState(s => ({ ...s, eWayBillMandateAll: !s.eWayBillMandateAll }))}
              disabled={!state.eWayBillEnabled}
            />
            <div>
              <label className={lbl}>Threshold (₹) — dispatches ≥ this value require e-Way Bill</label>
              <input
                type="number"
                min={0}
                className={inp}
                value={state.eWayBillThreshold}
                onChange={e => setState(s => ({ ...s, eWayBillThreshold: Number(e.target.value) || 0 }))}
              />
            </div>
          </div>
        </div>

        {/* ── GST Filing ──────────────────────────────────────────────────── */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-4 flex items-center gap-2">
            <FileText size={18} className="text-[var(--ink-0)]" />
            <h3 className="text-sm font-semibold text-gray-900">GST Filing</h3>
          </div>
          <ToggleRow
            label="Enable GSTR-1 / GSTR-3B preparation queue"
            description="Auto-collect confirmed invoices into the filing queue for the relevant period."
            checked={state.gstFilingEnabled}
            onChange={() => setState(s => ({ ...s, gstFilingEnabled: !s.gstFilingEnabled }))}
          />
        </div>

        {/* ── Additional GSTINs ───────────────────────────────────────────── */}
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-[var(--ink-0)]" />
              <h3 className="text-sm font-semibold text-gray-900">Additional GSTINs (multi-state)</h3>
            </div>
            <button
              type="button"
              onClick={addAdditionalGstin}
              className="rounded-lg border border-gray-300 px-3 py-1 text-xs hover:bg-gray-50"
            >
              + Add GSTIN
            </button>
          </div>
          {state.additionalGstins.length === 0 ? (
            <p className="text-xs text-gray-500">No additional GSTINs configured.</p>
          ) : (
            <div className="space-y-2">
              {state.additionalGstins.map((row, i) => (
                <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-end">
                  <div>
                    <label className={lbl}>GSTIN</label>
                    <input
                      className={inp}
                      value={row.gstin}
                      onChange={e => updateAdditionalGstin(i, { gstin: e.target.value.toUpperCase() })}
                      placeholder="22AAAAA0000A1Z5"
                    />
                  </div>
                  <div>
                    <label className={lbl}>Trade Name</label>
                    <input
                      className={inp}
                      value={row.tradeName ?? ''}
                      onChange={e => updateAdditionalGstin(i, { tradeName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className={lbl}>State</label>
                    <input
                      className={inp}
                      value={row.state ?? ''}
                      onChange={e => updateAdditionalGstin(i, { state: e.target.value })}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAdditionalGstin(i)}
                    className="rounded-lg border border-red-200 px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Toggle Row ───────────────────────────────────────────────────────────
function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <div className={`flex items-start justify-between gap-4 ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <button
        type="button"
        onClick={onChange}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors ${
          checked ? 'bg-[var(--ink-0)]' : 'bg-gray-300'
        } ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

export default GSTComplianceTab;
